import { getPlatformStyles } from '@direct-frontend/platform-code';
import { compact, mergeRefs } from '@direct-frontend/stdlib';
import React, { FC, useCallback, useMemo, useRef, useState } from 'react';

import { cn } from '../../lib/css/className/className';
import { getColorSkin } from '../../lib/skin/getColorSkin/getColorSkin';
import { getScrollbarClassName } from '../../lib/css/getScrollbarClassName';
import { getSizeSkin } from '../../lib/skin/getSizeSkin/getSizeSkin';
import { LayerFocusScope } from '../../lib/layerManager/components/LayerFocusScope/LayerFocusScope';
import { useVisualViewportScrollLock } from '../../hooks/useVisualViewportScrollLock/useVisualViewportScrollLock';
import { IconCloseSkinny as IconClose } from '../../icons/colorless';
import { ClickableIcon } from '../ClickableIcon';
import { Overlay } from '../Overlay';
import { OverlayContentLogProvider } from '../Overlay/Overlay';
import { HStack } from '../Stack';

import { useViewportModalResize } from './hooks/useViewportModalResize/useViewportModalResize';
import { i18n } from './Modal@desktop.i18n';
import { ButtonsProvider } from './ModalButtons';
import { ANIMATION_STATES, ModalAnimationState, ModalProps, ModalPropsWithMaxWidth } from './types';

const modalCloseButtonNode = { name: 'ModalCloseButton' };

getPlatformStyles({
    desktop: import('./Modal@desktop.css'),
});

export const cls = cn('Modal');

const scrollClass = getScrollbarClassName();

const CloseButton = (props: { onClick?: () => void; className?: string; 'data-testid'?: string }) => {
    const closeButtonText = i18n('Закрыть');
    return (
        <ClickableIcon
            className={props.className}
            onClick={props.onClick}
            color="current"
            clickableAreaSize="32"
            title={closeButtonText}
            aria-label={closeButtonText}
            logNode={modalCloseButtonNode}
            data-testid={`${props['data-testid'] ?? 'Modal'}.CloseButton`}
        >
            <IconClose size="16" />
        </ClickableIcon>
    );
};

/**
 * Type guard для проверки, что props имеет width='max'
 */
function isModalPropsWithMaxWidth(props: ModalProps): props is ModalPropsWithMaxWidth {
    return props.width === 'max';
}

/**
 * Компонент для отображения модального окна
 */
const Modal: FC<ModalProps> = function Modal(props) {
    const {
        className,
        closeButtonClassName,
        overlayClassName,
        children,
        isOpen = false,
        onClose,
        hasCloseButton = false,
        hasCloseButtonOnOverlay = false,
        dismissOnOutsideClick = true,
        bottomButtons,
        scrollable = true,
        containerClassName,
        'data-testid': dataTestid = 'Modal',
        position = 'top',
        paddingCorrection = 'top',
        containerRef,
        contentRef,
        containFocus = true,
        onAnimationEnd: onAnimationEndProp,
        portalContainer,
        logNode,
        isLoading,
        leftbar,
        rightbar,
        withoutPaddings = false,
        width,
        height,
        maxWidthNoLimit,
        ...overlayProps
    } = isModalPropsWithMaxWidth(props) ? props : { maxWidthNoLimit: undefined, ...props };

    const innerContentRef = useRef<HTMLDivElement>(null);

    // управление состояниями анимаций
    const [animationState, setAnimationState] = useState<ModalAnimationState>(
        isOpen ? ANIMATION_STATES.OPEN : ANIMATION_STATES.CLOSE,
    );

    if (isOpen) {
        // Если animationState === ANIMATION_STATES.NONE,
        // то модалка уже отображается и нам не нужно переключать в ANIMATION_STATES.OPEN
        if (animationState === ANIMATION_STATES.CLOSE) {
            setAnimationState(ANIMATION_STATES.OPEN);
        }
    } else if (animationState !== ANIMATION_STATES.CLOSE) {
        setAnimationState(ANIMATION_STATES.CLOSE);
    }

    const onCloseCanceled = useCallback(() => {
        setAnimationState(ANIMATION_STATES.MISCLICK);
    }, []);

    const onAnimationEnd = useCallback<React.AnimationEventHandler<HTMLElement>>(
        event => {
            // колбек следит за всеми дочерними элементами, поэтому проверяем, что сработало именно на корневом элементе
            if (event.target !== wrapperRef.current) {
                return;
            }

            onAnimationEndProp?.(animationState);

            // При завершения анимаций, после которых модалка продолжает отображаться, сбрасываем animation стиль
            if (animationState === ANIMATION_STATES.MISCLICK || animationState === ANIMATION_STATES.OPEN) {
                setAnimationState(ANIMATION_STATES.NONE);
            }
        },
        [animationState, onAnimationEndProp],
    );

    const scrollLockRef = useRef(null);
    const wrapperRef = useRef(null);

    // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
    const callbackRef = useMemo(() => mergeRefs(...compact([containerRef, scrollLockRef])), [containerRef]);

    useVisualViewportScrollLock({ enabled: false, ref: scrollLockRef });
    useViewportModalResize(wrapperRef);

    const autoFocusProps = useMemo(() => {
        return {
            contain: containFocus,
            restoreFocus: true,
            autoFocus: true,
        };
    }, [containFocus]);

    const skinClass = getColorSkin('gray-neutral', {});
    const skinSizeClass = getSizeSkin('layout');
    const sidebarClassName = cls('sidebar');

    return (
        <Overlay
            className={cls('overlay', [overlayClassName])}
            isOpen={isOpen}
            onClose={onClose}
            onCloseCanceled={onCloseCanceled}
            hasCloseButton={hasCloseButtonOnOverlay}
            dismissOnOutsideClick={dismissOnOutsideClick}
            data-testid={dataTestid}
            position={position}
            height={height}
            limitHeightByViewport={scrollable}
            containFocus={containFocus}
            autoFocus={false}
            innerContentRef={innerContentRef}
            portalContainer={portalContainer}
            {...overlayProps}
        >
            <OverlayContentLogProvider logNode={logNode} componentName="Modal">
                <div
                    className={cls(
                        'wrapper-layout',
                        {
                            width: width === 'max' ? 'max' : undefined,
                            limitWidth: width === 'max' && !maxWidthNoLimit,
                            height: height === 'max' ? 'max' : undefined,
                        },
                        [skinClass, skinSizeClass],
                    )}
                    data-testid={`${dataTestid}.WrapperLayout`}
                >
                    <div
                        data-testid={`${dataTestid}.Wrapper`}
                        className={cls('wrapper', { state: animationState, scrollable })}
                        onAnimationEnd={onAnimationEnd}
                        ref={wrapperRef}
                    >
                        <LayerFocusScope autoFocusProps={autoFocusProps} isOpen={isOpen}>
                            <ButtonsProvider>
                                <div
                                    className={cls('overflow-wrapper', { loading: isLoading })}
                                    data-loading-indicator={isLoading || undefined}
                                >
                                    <div
                                        ref={callbackRef}
                                        className={cls(
                                            {
                                                width: width === 'max' ? 'max' : undefined,
                                                height: height === 'max' ? 'max' : undefined,
                                            },
                                            [containerClassName],
                                        )}
                                        tabIndex={-1}
                                        focus-fallback="true"
                                    >
                                        {hasCloseButton && (
                                            <CloseButton
                                                onClick={onClose}
                                                className={cls('close-button', [closeButtonClassName])}
                                                data-testid={dataTestid}
                                            />
                                        )}
                                        {leftbar && (
                                            <div className={cls('leftbar', [scrollClass, sidebarClassName])}>
                                                {leftbar}
                                            </div>
                                        )}
                                        <div
                                            ref={mergeRefs(contentRef ?? null, innerContentRef)}
                                            className={cls(
                                                'inner',
                                                {
                                                    paddingCorrection,
                                                    scrollable,
                                                    withoutPaddings,
                                                    width: width === 'max',
                                                    height: height === 'max',
                                                },
                                                [className, scrollClass],
                                            )}
                                        >
                                            {children}
                                        </div>
                                        {rightbar && (
                                            <div className={cls('rightbar', [scrollClass, sidebarClassName])}>
                                                {rightbar}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </ButtonsProvider>
                            {/* Старые пропсы не через ModalButtons,
                             так как может быть бесконечный ререндер, если пропс уходил раньше в Suspense.
                             Переезд по месту использования на ModalButtons решает эту проблему */}
                            {Boolean(bottomButtons) && (
                                <HStack className={cls('bottom-buttons')} gap="8">
                                    {bottomButtons}
                                </HStack>
                            )}
                        </LayerFocusScope>
                    </div>
                </div>
            </OverlayContentLogProvider>
        </Overlay>
    );
};

export default Modal;
